import { useState } from 'react';
import type { TeamResponse } from '@orion/contracts';
import { Button, DataTable, Dialog, Input, Select } from '@orion/ui';
import { PageHeader } from '../../../app/shell.js';
import { ApiError } from '../../../lib/api-client.js';
import { disambiguateNames } from '../../../lib/disambiguate-names.js';
import { useToast } from '../../../lib/toast.js';
import { useAssignableMembersQuery } from '../../me/api.js';
import { useCreateTeam, useDeleteTeam, useTeamsQuery, useUpdateTeamMembers } from './api.js';

interface WorkingMember {
  membershipId: string;
  displayName: string;
}

/** Add/remove members for one team — PATCHes the full desired list;
 * `TeamsAdminService.setMembers` diffs it server-side and notifies/emails
 * only whoever actually changed (Admin ▸ Email & notifications' "Added to a
 * team" / "Removed from a team" rules govern whether that email sends). */
function ManageMembersDialog({ team, onClose }: { team: TeamResponse; onClose: () => void }) {
  const { data: allMembers } = useAssignableMembersQuery();
  const updateMembers = useUpdateTeamMembers();
  const toast = useToast();
  const [members, setMembers] = useState<WorkingMember[]>(team.members.map((m) => ({ membershipId: m.membershipId, displayName: m.displayName })));
  const [addId, setAddId] = useState('');

  const available = (allMembers ?? []).filter((m) => !members.some((w) => w.membershipId === m.id));
  const availableLabels = disambiguateNames(available);

  const addMember = () => {
    const pick = available.find((m) => m.id === addId);
    if (!pick) return;
    setMembers((prev) => [...prev, { membershipId: pick.id, displayName: pick.displayName }]);
    setAddId('');
  };

  const removeMember = (membershipId: string) => setMembers((prev) => prev.filter((m) => m.membershipId !== membershipId));

  const save = async () => {
    try {
      await updateMembers.mutateAsync({ id: team.id, membershipIds: members.map((m) => m.membershipId) });
      toast.success(`${team.name}'s members updated.`);
      onClose();
    } catch (err) {
      const detail = err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Failed to update members.';
      toast.error(detail);
    }
  };

  return (
    <Dialog
      open
      title={`Manage members — ${team.name}`}
      onClose={onClose}
      actions={
        <Button size="sm" onClick={save} loading={updateMembers.isPending}>
          Save changes
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-end gap-2">
          <Select
            label="Add a member"
            hideLabel
            value={addId}
            onChange={(e) => setAddId(e.target.value)}
            placeholder="Choose someone to add…"
            options={available.map((m) => ({ value: m.id, label: availableLabels.get(m.id) ?? m.displayName }))}
            className="flex-1"
          />
          <Button variant="secondary" size="sm" onClick={addMember} disabled={!addId}>
            Add
          </Button>
        </div>

        {members.length === 0 ? (
          <p className="text-sm text-text-muted">No members yet.</p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {members.map((m) => (
              <li key={m.membershipId} className="flex items-center justify-between rounded-control border border-border bg-ground px-3 py-2">
                <span className="text-sm font-medium text-ink">{m.displayName}</span>
                <button type="button" onClick={() => removeMember(m.membershipId)} className="text-xs font-semibold text-danger hover:underline">
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  );
}

export function TeamsPage() {
  const { data: teams, isLoading } = useTeamsQuery();
  const createTeam = useCreateTeam();
  const deleteTeam = useDeleteTeam();
  const toast = useToast();
  const [name, setName] = useState('');
  const [managingTeam, setManagingTeam] = useState<TeamResponse | null>(null);

  const add = async () => {
    if (!name.trim()) return;
    await createTeam.mutateAsync({ name });
    setName('');
  };

  const remove = async (team: TeamResponse) => {
    try {
      await deleteTeam.mutateAsync(team.id);
      toast.success(`${team.name} deleted.`);
    } catch (err) {
      const detail = err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Failed to delete team.';
      toast.error(detail);
    }
  };

  return (
    <>
      <PageHeader title="Teams" />
      <main className="flex flex-1 flex-col gap-4 p-7">
        <div className="flex gap-2">
          <Input label="Team name" hideLabel value={name} onChange={(e) => setName(e.target.value)} placeholder="New team name" />
          <Button size="sm" onClick={add} loading={createTeam.isPending}>
            Add team
          </Button>
        </div>

        {!isLoading && (teams ?? []).length > 0 && (
          <DataTable
            rows={teams ?? []}
            rowKey={(t) => t.id}
            columns={[
              { key: 'name', header: 'Name', width: '2fr', render: (t) => <span className="font-medium text-ink">{t.name}</span> },
              { key: 'members', header: 'Members', width: '1fr', render: (t) => t.memberCount },
              {
                key: 'actions',
                header: '',
                width: '220px',
                render: (t) => (
                  <div className="flex justify-end gap-3">
                    <button type="button" onClick={() => setManagingTeam(t)} className="text-[13px] font-semibold text-primary hover:underline">
                      Manage members
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(t)}
                      disabled={t.memberCount > 0}
                      title={t.memberCount > 0 ? 'Remove all members before deleting a team.' : undefined}
                      className="text-[13px] font-semibold text-danger hover:underline disabled:opacity-40 disabled:no-underline"
                    >
                      Delete
                    </button>
                  </div>
                ),
              },
            ]}
          />
        )}
      </main>

      {managingTeam && <ManageMembersDialog team={managingTeam} onClose={() => setManagingTeam(null)} />}
    </>
  );
}
